import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOMeta from '@/components/feature/SEOMeta';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { aboutInfo } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          params.append(key, String(value));
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9mvq78bdkpk99j7geo0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="关于奇优影院 - 品牌介绍 | 联系我们"
        description="奇优影院是国内领先的免费在线影视播放平台，致力于为影视爱好者提供最新最全的电影电视剧资源。了解奇优影院的品牌故事、发展历程及联系方式。"
        keywords="奇优影院,关于我们,品牌介绍,联系方式,免费影视平台"
        canonicalUrl="https://www.qiyoucinema.com/about"
      />
      <SchemaOrg type="Organization" data={{
        name: '奇优影院',
        url: 'https://www.qiyoucinema.com/',
        description: aboutInfo.intro,
        email: aboutInfo.contact.email,
        telephone: aboutInfo.contact.phone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: aboutInfo.contact.address,
          addressLocality: '北京',
          addressCountry: 'CN',
        },
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-[1200px] mx-auto">
              <Breadcrumb items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]} />
            </div>
          </div>

          {/* Brand Intro */}
          <section className="px-4 md:px-6 py-10 md:py-16 bg-background-50">
            <div className="max-w-[900px] mx-auto text-center">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-800">
                关于 <span className="text-primary-500">奇优影院</span>
              </h1>
              <p className="mt-4 text-lg text-foreground-600 font-medium">{aboutInfo.slogan}</p>
              <p className="mt-6 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
                {aboutInfo.intro}
              </p>
            </div>
          </section>

          {/* Mission & Vision */}
          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-[900px] mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500">
                    <i className="ri-focus-2-line text-lg"></i>
                  </div>
                  <h3 className="mt-4 text-base font-bold text-foreground-800">我们的使命</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{aboutInfo.mission}</p>
                </div>
                <div className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500/10 text-accent-500">
                    <i className="ri-eye-line text-lg"></i>
                  </div>
                  <h3 className="mt-4 text-base font-bold text-foreground-800">我们的愿景</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{aboutInfo.vision}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-[900px] mx-auto">
              <h2 className="text-lg font-heading font-bold text-foreground-800 text-center mb-8">核心价值观</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {aboutInfo.values.map((val) => (
                  <div key={val.title} className="p-5 rounded-lg bg-background-100 border border-background-200/70 text-center">
                    <h4 className="text-sm font-bold text-foreground-800">{val.title}</h4>
                    <p className="mt-2 text-xs text-foreground-600 leading-relaxed">{val.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-[900px] mx-auto">
              <h2 className="text-lg font-heading font-bold text-foreground-800 text-center mb-8">发展历程</h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300/60 -translate-x-1/2 hidden md:block"></div>
                <div className="space-y-6">
                  {aboutInfo.milestones.map((item, index) => (
                    <div key={item.year} className={`flex flex-col md:flex-row gap-4 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="md:w-1/2 flex md:justify-end items-center gap-3">
                        {index % 2 === 0 ? (
                          <>
                            <div className="flex-1 md:text-right">
                              <span className="text-lg font-heading font-bold text-primary-500">{item.year}</span>
                              <p className="text-sm text-foreground-600 mt-1">{item.event}</p>
                            </div>
                            <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                              <i className="ri-check-line text-sm text-background-50"></i>
                            </div>
                            <div className="hidden md:block md:w-1/2"></div>
                          </>
                        ) : (
                          <>
                            <div className="hidden md:block md:w-1/2"></div>
                            <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                              <i className="ri-check-line text-sm text-background-50"></i>
                            </div>
                            <div className="flex-1">
                              <span className="text-lg font-heading font-bold text-primary-500">{item.year}</span>
                              <p className="text-sm text-foreground-600 mt-1">{item.event}</p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-[900px] mx-auto">
              <h2 className="text-lg font-heading font-bold text-foreground-800 text-center mb-8">联系我们</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-base font-bold text-foreground-800 mb-4">联系方式</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">邮箱</p>
                        <p className="text-sm font-medium text-foreground-700">{aboutInfo.contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500">
                        <i className="ri-phone-line"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">电话</p>
                        <p className="text-sm font-medium text-foreground-700">{aboutInfo.contact.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500">
                        <i className="ri-wechat-line"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">微信</p>
                        <p className="text-sm font-medium text-foreground-700">{aboutInfo.contact.wechat}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">地址</p>
                        <p className="text-sm font-medium text-foreground-700">{aboutInfo.contact.address}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-base font-bold text-foreground-800 mb-4">给我们留言</h3>
                  <form
                    onSubmit={handleSubmit}
                    className="space-y-3"
                    data-readdy-form
                  >
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-600 mb-1">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2 text-sm rounded-md bg-background-100 border border-background-300/60 text-foreground-800 placeholder-foreground-500 focus:outline-none focus:border-primary-500/50 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-600 mb-1">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2 text-sm rounded-md bg-background-100 border border-background-300/60 text-foreground-800 placeholder-foreground-500 focus:outline-none focus:border-primary-500/50 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-600 mb-1">留言内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2 text-sm rounded-md bg-background-100 border border-background-300/60 text-foreground-800 placeholder-foreground-500 focus:outline-none focus:border-primary-500/50 transition-all resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                    >
                      {formStatus === 'loading' ? '提交中...' : '提交留言'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-500 text-center">感谢您的留言，我们会尽快回复！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}